import { useAuth } from "../context/AuthContext";
import { useAurelioBuilder } from "../context/AurelioBuilderContext";
import { useState } from "react";
import Widget from "../components/dashboard/Widget";
import QuickAccess from "../components/dashboard/QuickAccess";
import RecentProjects from "../components/dashboard/RecentProjects";
import { Link } from "react-router-dom";

function StatCard({ title, value, icon, trend = null, color = "bg-[#ff1b6d]" }) {
  return (
    <div className="bg-[#1e1e1e] overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`${color} rounded-md p-3`}>
              {icon}
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-400 truncate">
                {title}
              </dt>
              <dd className="flex items-baseline">
                <div className="text-2xl font-semibold text-white">
                  {value}
                </div>
                {trend && (
                  <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                    trend.type === 'increase' ? 'text-green-500' : 'text-red-500'
                  }`}>
                    <svg className="self-center flex-shrink-0 h-5 w-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d={
                        trend.type === 'increase'
                          ? "M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                          : "M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
                      } clipRule="evenodd" />
                    </svg>
                    <span className="sr-only">
                      {trend.type === 'increase' ? 'Increased' : 'Decreased'} by
                    </span>
                    {trend.value}
                  </div>
                )}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActivityItem({ title, description, date, icon }) {
  return (
    <div className="flex space-x-3 pb-5">
      <div className="flex-shrink-0">
        <div className="h-8 w-8 rounded-full bg-[#ff1b6d] flex items-center justify-center">
          {icon}
        </div>
      </div>
      <div className="flex-1 space-y-1">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-white">{title}</h3>
          <p className="text-xs text-gray-400">{date}</p>
        </div>
        <p className="text-sm text-gray-400">{description}</p>
      </div>
    </div>
  );
}

function Dashboard() {
  const { user } = useAuth();
  const { isFullscreen, editorData, resetState } = useAurelioBuilder();
  const [timeRange, setTimeRange] = useState("week");
  
  // Log del estado para debugging
  console.log('Dashboard - AurelioBuilder state:', { isFullscreen, editorData });

  const widgetAction = (label, onClick) => (
    <button
      onClick={onClick}
      className="text-xs font-medium text-[#ff1b6d] hover:text-[#ff1b6d]/80 focus:outline-none"
    >
      {label}
    </button>
  );

  const timeRangeSelector = (
    <div className="inline-flex rounded-md shadow-sm">
      <button
        type="button"
        className={`px-3 py-1 text-xs font-medium rounded-l-md ${
          timeRange === "week"
            ? "bg-[#ff1b6d] text-white"
            : "bg-[#252525] text-gray-400 hover:bg-[#2a2a2a]"
        }`}
        onClick={() => setTimeRange("week")}
      >
        Semana
      </button>
      <button
        type="button"
        className={`px-3 py-1 text-xs font-medium ${
          timeRange === "month"
            ? "bg-[#ff1b6d] text-white"
            : "bg-[#252525] text-gray-400 hover:bg-[#2a2a2a]"
        }`}
        onClick={() => setTimeRange("month")}
      >
        Mes
      </button>
      <button
        type="button"
        className={`px-3 py-1 text-xs font-medium rounded-r-md ${
          timeRange === "year"
            ? "bg-[#ff1b6d] text-white"
            : "bg-[#252525] text-gray-400 hover:bg-[#2a2a2a]"
        }`}
        onClick={() => setTimeRange("year")}
      >
        Año
      </button>
    </div>
  );

  return (
    <div className="py-6">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">
          Bienvenido, {user?.name || user?.email}
        </h1>
        <p className="mt-1 text-gray-400">
          Este es el resumen de tu actividad y proyectos recientes.
        </p>
      </div>

      {/* Tools Section */}
      <div className="mb-6">
        <Widget 
          title="Herramientas" 
          titleAction={widgetAction("Ver Todas", () => {})}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* Image Optimizer Card */}
            <Link 
              to="/dashboard/tools/image-optimizer"
              className="block bg-[#1e1e1e] rounded-lg overflow-hidden transition-all duration-200 hover:shadow-lg hover:shadow-[#ff1b6d]/10 hover:translate-y-[-4px] hover:bg-[#252525]"
            >
              <div className="p-5">
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 bg-[#ff1b6d]/10 p-4 rounded-full">
                    <svg className="h-10 w-10 text-[#ff1b6d]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Optimizador de Imágenes</h3>
                  <p className="text-gray-400 text-sm">
                    Optimiza tus imágenes para web, reduce su tamaño y mejora la velocidad de carga.
                  </p>
                  <span className="mt-4 inline-flex items-center text-sm font-medium text-[#ff1b6d]">
                    Usar herramienta
                    <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </span>
                </div>
              </div>
            </Link>

            {/* YouTube Downloader Card */}
            <Link 
              to="/dashboard/tools/youtube-downloader"
              className="block bg-[#1e1e1e] rounded-lg overflow-hidden transition-all duration-200 hover:shadow-lg hover:shadow-[#ff0000]/10 hover:translate-y-[-4px] hover:bg-[#252525]"
            >
              <div className="p-5">
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 bg-[#ff0000]/10 p-4 rounded-full">
                    <svg className="h-10 w-10 text-[#ff0000]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">YouTube Downloader</h3>
                  <p className="text-gray-400 text-sm">
                    Descarga videos de YouTube en diferentes formatos y calidades para uso personal.
                  </p>
                  <span className="mt-4 inline-flex items-center text-sm font-medium text-[#ff0000]">
                    Usar herramienta
                    <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </span>
                </div>
              </div>
            </Link>

            {/* PDF Tools Card */}
            <Link 
              to="/dashboard/tools/pdf-tools"
              className="block bg-[#1e1e1e] rounded-lg overflow-hidden transition-all duration-200 hover:shadow-lg hover:shadow-[#007bff]/10 hover:translate-y-[-4px] hover:bg-[#252525]"
            >
              <div className="p-5">
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 bg-[#007bff]/10 p-4 rounded-full">
                    <svg className="h-10 w-10 text-[#007bff]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">PDF Tools</h3>
                  <p className="text-gray-400 text-sm">
                    Herramientas para manipular, convertir y combinar archivos PDF de forma sencilla.
                  </p>
                  <span className="mt-4 inline-flex items-center text-sm font-medium text-[#007bff]">
                    Usar herramienta
                    <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </span>
                </div>
              </div>
            </Link>

            {/* SEO Mass Card */}
            <Link 
              to="/dashboard/tools/seo-mass-generator"
              className="block bg-[#1e1e1e] rounded-lg overflow-hidden transition-all duration-200 hover:shadow-lg hover:shadow-[#10b981]/10 hover:translate-y-[-4px] hover:bg-[#252525]"
            >
              <div className="p-5">
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 bg-[#10b981]/10 p-4 rounded-full">
                    <svg className="h-10 w-10 text-[#10b981]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 11l5-5m0 0l5 5m-5-5v12" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7h16" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">SEO Mass</h3>
                  <p className="text-gray-400 text-sm">
                    Analiza y mejora el posicionamiento de tu sitio web con herramientas avanzadas de SEO.
                  </p>
                  <span className="mt-4 inline-flex items-center text-sm font-medium text-[#10b981]">
                    Usar herramienta
                    <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </span>
                </div>
              </div>
            </Link>
            
            {/* Aurelio Builder Card */}
            <Link 
              to="/dashboard/tools/aurelio-builder"
              className="block bg-[#1e1e1e] rounded-lg overflow-hidden transition-all duration-200 hover:shadow-lg hover:shadow-[#8b5cf6]/10 hover:translate-y-[-4px] hover:bg-[#252525]"
            >
              <div className="p-5">
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 bg-[#8b5cf6]/10 p-4 rounded-full">
                    <svg className="h-10 w-10 text-[#8b5cf6]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Aurelio Builder</h3>
                  <p className="text-gray-400 text-sm">
                    Construye páginas web profesionales con nuestro maquetador drag & drop.
                  </p>
                  <span className="mt-4 inline-flex items-center text-sm font-medium text-[#8b5cf6]">
                    Usar herramienta
                    <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </span>
                </div>
              </div>
            </Link>

            {/* Placeholder for future tools */}
            <div className="block bg-[#1e1e1e] rounded-lg overflow-hidden border border-dashed border-gray-700 opacity-60">
              <div className="p-5">
                <div className="flex flex-col items-center text-center justify-center min-h-[224px]">
                  <svg className="h-10 w-10 text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <p className="text-gray-600">
                    Próximamente más herramientas
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Widget>
      </div>

      {/* Debug Widget - TEMPORAL */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mb-6">
          <Widget title="🔧 Debug - AurelioBuilder State">
            <div className="bg-[#2a2a2a] rounded p-4">
              <div className="text-sm text-gray-300 mb-3">
                <strong>Estado actual:</strong>
                <pre className="mt-2 text-xs">
                  {JSON.stringify({ isFullscreen, editorData }, null, 2)}
                </pre>
              </div>
              <button 
                onClick={() => {
                  resetState();
                  window.location.reload();
                }}
                className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
              >
                🗑️ Reset State & Reload
              </button>
            </div>
          </Widget>
        </div>
      )}

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {/* Statistics Cards */}
        <div className="md:col-span-2 lg:col-span-3">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              title="Proyectos Activos"
              value="12"
              trend={{ type: "increase", value: "10%" }}
              icon={
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              }
            />
            <StatCard
              title="Tareas Pendientes"
              value="8"
              trend={{ type: "decrease", value: "5%" }}
              icon={
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              }
            />
            <StatCard
              title="Tareas Completadas"
              value="24"
              trend={{ type: "increase", value: "24%" }}
              icon={
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
          </div>
        </div>

        {/* Quick Access Section */}
        <div className="xl:row-span-2">
          <Widget title="Accesos Rápidos">
            <QuickAccess />
          </Widget>
        </div>

        {/* Activity Timeline */}
        <div className="md:col-span-2 lg:col-span-3">
          <Widget 
            title="Actividad Reciente" 
            titleAction={widgetAction("Ver Todo", () => {})}
          >
            <div className="space-y-5">
              <ActivityItem 
                title="Nuevo proyecto creado"
                description="Se ha creado el proyecto WeMax App"
                date="Hace 2 horas"
                icon={
                  <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                }
              />
              <div className="border-t border-[#2a2a2a]"></div>
              <ActivityItem 
                title="Tarea completada"
                description="Configuración inicial del proyecto"
                date="Hace 3 horas"
                icon={
                  <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                }
              />
              <div className="border-t border-[#2a2a2a]"></div>
              <ActivityItem 
                title="Actualización del sistema"
                description="Se han actualizado las dependencias"
                date="Hace 5 horas"
                icon={
                  <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                }
              />
            </div>
          </Widget>
        </div>
        
        {/* Recent Projects Section */}
        <div className="md:col-span-2 xl:col-span-3">
          <Widget 
            title="Proyectos Recientes" 
            titleAction={widgetAction("Ver Todos", () => {})}
          >
            <RecentProjects />
          </Widget>
        </div>
        
        
        {/* Analytics Preview Widget */}
        <div className="md:col-span-2 xl:col-span-4">
          <Widget 
            title="Análisis de Rendimiento" 
            titleAction={timeRangeSelector}
          >
            <div className="h-64 w-full flex items-center justify-center border border-[#2a2a2a] rounded-lg">
              <p className="text-gray-400">Gráfico de análisis (Placeholder)</p>
            </div>
          </Widget>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;